import { useMemo, useState } from 'react';
import { Alert, Badge, Chip, InfoTip, SearchCombobox, type DataTableColumn } from '@zarya/ui';
import { t } from '@app/i18n';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import { CapabilityGrid } from './CapabilityGrid';
import {
  warningOutsideCatalog,
  warningManualResource,
  warningRiskUnknown,
  notAcceptsLimit,
  resolutionTip,
  risk,
  limitsText,
  reasonOutsideCatalog,
  resolutionByCatalog,
  titleOutsideCatalog,
  type AccessControlState,
  type ToolRow,
} from './AccessControlModel';
import type { AccessDraftApi } from './useAccessControlDraft';
import s from './AccessControl.module.css';

export interface ToolsTabProps {
  estado: AccessControlState;
  rascunho: AccessDraftApi;
  /** `false` para viewer: a grade fica em leitura (FR-010). */
  editavel: boolean;
}

/**
 * Aba **Tools** (FR-072, FR-073): marcar/desmarcar tools do catálogo por nome no
 * PDP e definir os limites que a tool aceita.
 *
 * Campo de limite só existe para parâmetro em `constrainable` — as demais dizem
 * "não aceita limite". É o produto recusando inventar constraint que o executor
 * não conhece.
 */
export function ToolsTab({ estado, rascunho, editavel }: ToolsTabProps) {
  // `useMultiSite` e não uma prop: a revelação progressiva é a mesma regra de T4, T6 e
  // do `IdentityListPane`, e não é decisão de quem chama esta aba. Com um cluster só, a
  // coluna repetiria o mesmo valor em toda linha — ruído que empurra as colunas úteis
  // para fora da tela.
  const multiSite = useMultiSite();
  const [search, setBusca] = useState('');
  const [resource, setResource] = useState<string | null>(null);
  const [soConcedidas, setSoConcedidas] = useState(false);

  const resources = useMemo(() => {
    const mapping = new Map<string, string>();
    estado.tools.forEach((tool) => mapping.set(tool.resource_id, tool.resource_name));
    return [...mapping].map(([value, nome]) => ({ value, label: nome }));
  }, [estado.tools]);

  const filtradas = useMemo(() => {
    const term = search.trim().toLowerCase();
    return estado.tools.filter((tool) => {
      const concedida = rascunho.draft.tools[tool.tool_id]?.concedida ?? tool.granted;
      return (
        (!term ||
          `${tool.tool_name} ${tool.resource_name} ${tool.resource_gateway_id}`
            .toLowerCase()
            .includes(term)) &&
        (!resource || tool.resource_id === resource) &&
        (!soConcedidas || concedida)
      );
    });
  }, [search, estado.tools, rascunho.draft.tools, resource, soConcedidas]);

  const columns: DataTableColumn<ToolRow>[] = [
    {
      key: 'concedida',
      header: '',
      width: '44px',
      cell: (tool) => (
        <input
          type="checkbox"
          className={s.checkbox}
          checked={rascunho.draft.tools[tool.tool_id]?.concedida ?? false}
          disabled={!editavel}
          aria-label={t('identityAccess.tools.grant.aria', {
            capability: `${tool.resource_gateway_id}/${tool.tool_name}`,
          })}
          onChange={() => rascunho.alternarTool(tool.tool_id)}
        />
      ),
    },
    {
      key: 'tool',
      header: t('identityAccess.tools.col.tool'),
      cell: (tool) => {
        const concedida = rascunho.draft.tools[tool.tool_id]?.concedida ?? false;
        return (
          <span className={s.tool}>
            <b>{tool.tool_name}</b>
            {concedida && tool.risk === 'desconhecido' ? (
              <span className={s.aviso}>{warningRiskUnknown()}</span>
            ) : null}
            {concedida && tool.resource_origin === 'manual' ? (
              <span className={s.aviso}>{warningManualResource()}</span>
            ) : null}
            {/*
              O recibo da resolução pelo catálogo (30/08), na própria linha: é aqui
              que a pergunta nasce — "o meu `agent.yaml` diz outra coisa". Fora da
              linha ele viraria nota de rodapé sobre uma capability que o leitor já
              passou.
            */}
            {tool.declared_as ? (
              <span className={s.resolvida}>
                {resolutionByCatalog(tool.declared_as)}{' '}
                <InfoTip
                  text={resolutionTip()}
                  label={t('identityAccess.resolution.tipLabel')}
                  size="xs"
                />
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'resource',
      header: t('identityAccess.tools.col.resource'),
      cell: (tool) => (
        <span className={s.resource}>
          {tool.resource_name}
          <Badge tone="neutral">
            {tool.resource_kind ?? t('identityAccess.tools.resourceKindFallback')}
          </Badge>
          {tool.resource_origin === 'manual' ? (
            <Badge tone="warning">{t('identityAccess.tools.manual')}</Badge>
          ) : null}
        </span>
      ),
    },
    /*
      A COLUNA DE SITE É UM AVISO, NÃO UM ENFEITE (§3.7 da especificação do Site)

      Esta aba oferece o catálogo do TENANT inteiro, e isso é deliberado: o cross-site
      é legítimo por decisão do mrolim (11/09). Só que o produto de hoje RECUSA a
      concessão cross-site na aprovação — `422 tool_nao_catalogada` —, e a recusa não
      diz a palavra «site» em lado nenhum. Esta coluna é o único lugar onde quem
      concede vê de que cluster é a tool.

      Fica ao lado de «Resource» porque o site é do RESOURCE, não da tool: as duas se
      leem juntas, como em T4 o site se lê junto do SPIFFE ID.
    */
    ...(multiSite
      ? [
          {
            key: 'site',
            header: t('identityAccess.tools.col.site'),
            tip: t('identityAccess.tools.col.site.tip'),
            // O SLUG, como em T4, T6 e no painel de identidades — nunca o `name`.
            // Ausente é «não sei» (cadastro de sites que não respondeu), e o traço
            // diz isso sem afirmar um site: `site_slug` está fora de `required`
            // justamente para esta linha existir.
            cell: (tool: ToolRow) =>
              tool.site_slug ? <Chip>{tool.site_slug}</Chip> : <span>—</span>,
          } satisfies DataTableColumn<ToolRow>,
        ]
      : []),
    {
      key: 'risco',
      header: t('identityAccess.tools.col.risk'),
      cell: (tool) => {
        const risco = risk(tool.risk ?? 'desconhecido');
        return <Badge tone={risco.tone}>{risco.rotulo}</Badge>;
      },
    },
    {
      key: 'limite',
      header: t('identityAccess.tools.col.limit'),
      cell: (tool) => <FieldLimit tool={tool} rascunho={rascunho} editavel={editavel} />,
    },
  ];

  return (
    <CapabilityGrid
      label={t('identityAccess.tools.label')}
      columns={columns}
      rows={filtradas}
      rowKey={(tool) => tool.tool_id}
      search={search}
      onSearchChange={setBusca}
      searchPlaceholder={t('identityAccess.tools.searchPlaceholder')}
      extraFilters={
        <div style={{ minWidth: 220 }}>
          <SearchCombobox
            label={t('identityAccess.tools.resourceFilter.label')}
            placeholder={t('identityAccess.tools.resourceFilter.placeholder')}
            searchPlaceholder={t('identityAccess.tools.resourceFilter.searchPlaceholder')}
            options={resources}
            value={resource}
            onChange={setResource}
            size="sm"
          />
        </div>
      }
      grantedOnly={soConcedidas}
      onGrantedOnlyChange={setSoConcedidas}
      countLabel={t('identityAccess.tools.count', {
        n: filtradas.length,
        resources: resources.length,
      })}
      onClearFilters={() => {
        setBusca('');
        setResource(null);
        setSoConcedidas(false);
      }}
      filtersApplied={Boolean(search || resource || soConcedidas)}
      emptyIcon="server"
      emptyTitle={t('identityAccess.tools.empty.title')}
      emptyMessage={t('identityAccess.tools.empty.message')}
    >
      <OutsideCatalog itens={estado.out_of_catalog} />
    </CapabilityGrid>
  );
}

/**
 * As capabilities que o contrato concede e esta aba não consegue desenhar
 * (30/08).
 *
 * A aba Tools é o **catálogo** marcado pelo contrato. Uma capability que nenhuma
 * linha do catálogo representa — a tool declarada por uma release antes de o
 * catálogo ser importado, ou o que sobra de um resource excluído — não tem caixa,
 * e sem este bloco ela simplesmente **some**: a tela passa a afirmar, por omissão,
 * que o agente não tem aquela autoridade. No laboratório apareceu inteiro, com duas
 * identidades exibindo zero permissão enquanto o PDP concedia uma tool a cada uma.
 *
 * Somente leitura, e o texto diz por quê: sem linha no catálogo não há
 * `resource_id`/`tool_id`, que é como o candidato é chaveado. O backend as preserva
 * sozinho quando alguém salva — esta tela nunca as remove por omissão.
 */
function OutsideCatalog({ itens }: { itens: AccessControlState['out_of_catalog'] }) {
  if (!itens?.length) return null;

  return (
    // `role="status"`, não `alert`: é aviso **de estado**, persistente enquanto a
    // identidade for esta — não é o erro de uma ação. Com `alert` ele ocupa o papel
    // que todo seletor de "banner de erro" procura (o teste do `limite_nao_aceito`
    // tropeçou nele na primeira versão), exatamente como o `MiddlewareDownNotice`
    // documenta em `Alert`.
    <Alert tone="warning" role="status" title={titleOutsideCatalog(itens.length)}>
      <p>{warningOutsideCatalog()}</p>
      <ul className={s.foraDoCatalogo}>
        {itens.map((item) => (
          <li key={item.capability}>
            <code>{item.capability}</code>
            {Object.keys(item.constraints ?? {}).length ? (
              <span className={s.muted}> · {limitsText(item.constraints)}</span>
            ) : null}
            {/*
              O motivo por linha: os dois casos são diferentes e o remédio também.
              Um texto único aqui foi o defeito de 30/08 — dizia «o catálogo não
              conhece» sobre uma tool que a grade listava três linhas abaixo.
            */}
            <span className={s.muted}> — {reasonOutsideCatalog(item)}</span>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * Limite por parâmetro (FR-073). Sem `constrainable` não há campo: a coluna diz
 * "não aceita limite" e ninguém digita um teto que o PDP ignoraria.
 */
function FieldLimit({
  tool,
  rascunho,
  editavel,
}: {
  tool: ToolRow;
  rascunho: AccessDraftApi;
  editavel: boolean;
}) {
  if (!tool.constrainable.length) {
    return <span className={s.muted}>{notAcceptsLimit()}</span>;
  }

  const draft = rascunho.draft.tools[tool.tool_id];
  const concedida = draft?.concedida ?? false;

  return (
    <span className={s.limite}>
      {tool.constrainable.map((parametro) => {
        const erro = rascunho.erros[`${tool.tool_id}:${parametro}`];
        return (
          <span key={parametro}>
            <span className={s.limiteCampo}>
              <input
                type="text"
                inputMode="decimal"
                value={draft?.limites[parametro] ?? ''}
                placeholder={t('identityAccess.tools.limit.placeholder')}
                disabled={!editavel || !concedida}
                aria-label={t('identityAccess.tools.limit.aria', {
                  param: parametro,
                  tool: tool.tool_name,
                })}
                aria-invalid={erro ? true : undefined}
                onChange={(e) => rascunho.definirLimite(tool.tool_id, parametro, e.target.value)}
              />
              <span className={s.limiteParametro}>{parametro}</span>
            </span>
            {erro ? <span className={s.erro}>{erro}</span> : null}
          </span>
        );
      })}
    </span>
  );
}
